import { Injectable } from '@angular/core';
import {
    BuildingPartial,
    CurrentHeatingSystemPartial,
    HeatPumpToolsRequestPost,
    HeatPumpToolsResponse,
    UpdateProjectRequestPartial
} from '@nx-customer-apps/api-planning-projects';
import { LodashService } from '@nx-customer-apps/shared/services';
import { BuildingType, FormKeys, HeatGeneratorType, ProjectType } from '@nx-customer-apps/shared/enums';
import { AppState } from '@nx-customer-apps/shared/interfaces';
import { DEFAULT_EXHAUST_GAS_LOSSES } from '../../shared/utils/constants';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediaryMapper {
    public stateToProjectRequest(state: AppState): UpdateProjectRequestPartial {
        const {
            project: { selectedProject },
            heatPumpIntermediary: {
                building,
                livingArea,
                heatingDistribution,
                heatingEnergySource,
                energyConsumption,
                heatGeneratorDetails,
                isOldDeviceUsed,
                subsidyEligibility
            }
        } = state;

        const currentHeatingSystem: CurrentHeatingSystemPartial = {};

        if (heatGeneratorDetails?.[FormKeys.BoilerType]) {
            currentHeatingSystem.heatingSystemType = heatGeneratorDetails[FormKeys.BoilerType];
        }
        if (heatingEnergySource?.selectedEnergySource) {
            currentHeatingSystem.energySource = heatingEnergySource.selectedEnergySource.type;
        }
        if (heatGeneratorDetails?.[FormKeys.InstallationYear]) {
            currentHeatingSystem.installationConstructionYear = +heatGeneratorDetails[FormKeys.InstallationYear];
        }
        if (heatGeneratorDetails?.[FormKeys.LowTemperature]) {
            currentHeatingSystem.isLowTemperature = heatGeneratorDetails?.[FormKeys.LowTemperature];
        }
        if (heatGeneratorDetails?.[FormKeys.NominalPower]) {
            currentHeatingSystem.heatGeneratorPower = heatGeneratorDetails[FormKeys.NominalPower];
        }
        if (!LodashService.isNil(heatGeneratorDetails?.[FormKeys.GasLossExhaust])) {
            currentHeatingSystem.exhaustGasLosses = heatGeneratorDetails![FormKeys.GasLossExhaust];
        }
        if (!LodashService.isNil(heatGeneratorDetails?.[FormKeys.HeatPumpOperatingMode])) {
            currentHeatingSystem.heatPumpOperatingMode = heatGeneratorDetails![FormKeys.HeatPumpOperatingMode];
        }
        if (!LodashService.isNil(isOldDeviceUsed?.value)) {
            currentHeatingSystem.currentHeaterAsPeakLoadDevice = isOldDeviceUsed!.value;
        }

        const buildingPartial: BuildingPartial = {};

        if (Object.keys(currentHeatingSystem).length) {
            buildingPartial.currentHeatingSystem = currentHeatingSystem;
        }

        if (building?.[FormKeys.BuildingConstructionYear]) {
            buildingPartial.buildingConstructionYear = +building[FormKeys.BuildingConstructionYear];
        }
        if (heatingDistribution?.[FormKeys.DistributionMethod]) {
            buildingPartial.heatingType = heatingDistribution[FormKeys.DistributionMethod];
        }
        if (heatingDistribution?.[FormKeys.HeatingFlowTemperature]) {
            buildingPartial.flowTemperature = heatingDistribution[FormKeys.HeatingFlowTemperature];
        }
        if (energyConsumption?.[FormKeys.NonHeatingElectricityPerYear]) {
            buildingPartial.electricityDemand = energyConsumption[FormKeys.NonHeatingElectricityPerYear];
        }
        if (heatingDistribution?.[FormKeys.HeatingSchedule]) {
            buildingPartial.consumptionProfileForHeating = heatingDistribution[FormKeys.HeatingSchedule];
        }
        if (heatGeneratorDetails?.[FormKeys.WarmWaterByHeatGenerator]) {
            buildingPartial.warmWaterByHeatGenerator = heatGeneratorDetails[FormKeys.WarmWaterByHeatGenerator];
        }
        if (livingArea) {
            buildingPartial.livingArea = livingArea.value;
        }

        if (
            selectedProject?.building?.buildingType === BuildingType.MultiFamilyHouse &&
            !LodashService.isNil(subsidyEligibility?.numberOfResidentialUnits?.value)
        ) {
            buildingPartial.numberOfResidentialUnits = subsidyEligibility!.numberOfResidentialUnits!.value;
            return { building: buildingPartial };
        }

        if (
            LodashService.isNil(subsidyEligibility?.isIncomeLowForSubsidy?.value) ||
            LodashService.isNil(subsidyEligibility?.isOwnerOfTheBuilding?.value) ||
            LodashService.isNil(subsidyEligibility?.isOwnerMainResidence?.value)
        ) {
            return { building: buildingPartial };
        }

        buildingPartial.household = {
            isIncomeLowForSubsidy: subsidyEligibility?.isIncomeLowForSubsidy?.value,
            isOwnerOfTheBuilding: subsidyEligibility?.isOwnerOfTheBuilding?.value,
            isOwnerMainResidence: subsidyEligibility?.isOwnerMainResidence?.value
        };

        if (
            selectedProject?.projectType === ProjectType.Renovation &&
            !LodashService.isNil(subsidyEligibility?.isOldHeatingSystemDefective?.value)
        ) {
            buildingPartial.household = {
                ...buildingPartial.household,
                isOldHeatingSystemDefective: subsidyEligibility?.isOldHeatingSystemDefective?.value
            };
        }

        return { building: buildingPartial };
    }

    public toHeatPumpToolsRequest(state: AppState): HeatPumpToolsRequestPost {
        const {
            project: { selectedProject },
            heatPumpIntermediary: { heatingEnergySource, energyConsumption, heatGeneratorDetails }
        } = state;
        const countryCode = selectedProject!.address.countryCode;

        const heatGenerator =
            heatingEnergySource?.selectedHeatGenerator || selectedProject?.building.currentHeatingSystem?.heatGeneratorType;
        // New building project
        if (selectedProject?.projectType === ProjectType.NewConstruction) {
            return {
                countryCode,
                postalCode: selectedProject?.address.postalCode,
                projectType: selectedProject?.projectType!
            } as HeatPumpToolsRequestPost;
        }
        // Modernisation project (HP as an old device)
        else if (heatGenerator === HeatGeneratorType.HeatPump) {
            return {
                projectType: selectedProject?.projectType!,
                energySource:
                    heatingEnergySource?.selectedEnergySource?.type || selectedProject?.building.currentHeatingSystem?.energySource,
                energyPrices: selectedProject?.energyPrices,
                electricityDemand: energyConsumption?.nonHeatingElectricityPerYear || selectedProject?.building.electricityDemand,
                consumption: selectedProject?.building?.currentHeatingSystem?.consumption,
                postalCode: selectedProject?.address.postalCode!,
                countryCode,
                numberOfPersons: selectedProject!.building.numberOfPersons,
                installationConstructionYear:
                    +heatGeneratorDetails?.installationYear! ||
                    selectedProject?.building.currentHeatingSystem?.installationConstructionYear,
                heatGeneratorPower:
                    heatGeneratorDetails?.nominalPower || selectedProject?.building.currentHeatingSystem?.heatGeneratorPower,
                exhaustGasLosses: heatGeneratorDetails?.gasLossExhaust || DEFAULT_EXHAUST_GAS_LOSSES,
                heatingSystemType: heatGeneratorDetails?.boilerType || selectedProject?.building.currentHeatingSystem?.heatingSystemType,
                isLowTemperature:
                    !!heatGeneratorDetails?.lowTemperature || !!selectedProject?.building.currentHeatingSystem?.heatPumpOperatingMode,
                heatPumpOperatingMode:
                    heatGeneratorDetails?.heatPumpOperatingMode || selectedProject?.building.currentHeatingSystem?.heatPumpOperatingMode
            } as HeatPumpToolsRequestPost;
        }
        // Modernisation project (no HP as an old device)
        else {
            return {
                projectType: selectedProject?.projectType!,
                energySource:
                    heatingEnergySource?.selectedEnergySource?.type || selectedProject?.building.currentHeatingSystem?.energySource,
                energyPrices: selectedProject?.energyPrices,
                electricityDemand: energyConsumption?.nonHeatingElectricityPerYear || selectedProject?.building.electricityDemand,
                consumption: selectedProject?.building.currentHeatingSystem?.consumption,
                postalCode: selectedProject?.address.postalCode!,
                countryCode,
                numberOfPersons: selectedProject!.building.numberOfPersons,
                installationConstructionYear:
                    +heatGeneratorDetails?.installationYear! ||
                    selectedProject?.building.currentHeatingSystem?.installationConstructionYear,
                heatGeneratorPower:
                    heatGeneratorDetails?.nominalPower || selectedProject?.building.currentHeatingSystem?.heatGeneratorPower,
                exhaustGasLosses: heatGeneratorDetails?.gasLossExhaust || DEFAULT_EXHAUST_GAS_LOSSES,
                heatingSystemType: heatGeneratorDetails?.boilerType || selectedProject?.building.currentHeatingSystem?.heatingSystemType,
                isLowTemperature: !!heatGeneratorDetails?.lowTemperature
            } as HeatPumpToolsRequestPost;
        }
    }

    public heatPumpToolsToProjectRequest(heatPumpToolsParams: HeatPumpToolsResponse) {
        const building = {
            thermalCapacity: heatPumpToolsParams.thermalCapacity,
            heatingLimitTemperature: heatPumpToolsParams.heatingLimitTemperature,
            outsideTemperature: heatPumpToolsParams.outsideTemperature,
            currentHeatingSystem: {
                annualHeatingCosts: heatPumpToolsParams.annualHeatingCosts,
                annualElectricityCosts: heatPumpToolsParams.annualElectricityCosts,
                maxFlowTemp: heatPumpToolsParams.maxFlowTemp,
                minFlowTemp: heatPumpToolsParams.minFlowTemp,
                electricPerformance: heatPumpToolsParams.electricPerformance,
                efficiencyHeating: heatPumpToolsParams.efficiencyHeating,
                efficiencyWarmWater: heatPumpToolsParams.efficiencyWarmWater,
                electricityConsumptionForHeating: heatPumpToolsParams.electricityConsumptionForHeating,
                electricityConsumptionForWarmWater: heatPumpToolsParams.electricityConsumptionForWarmWater,
                standbyHeatLoss: heatPumpToolsParams.standbyHeatLoss,
                co2EmissionTotal: heatPumpToolsParams.co2EmissionTotal,
                co2EmissionForElectricity: heatPumpToolsParams.co2EmissionForElectricity,
                co2EmissionForHeating: heatPumpToolsParams.co2EmissionForHeating
            }
        };
        return { building };
    }
}
